import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Download,
  CheckCircle2,
  ArrowUpRight,
  Truck,
  MapPin,
  FileSignature,
  ClipboardCheck,
  Home,
  Stethoscope,
  BookOpen,
  Landmark,
  Info,
  HandHeart,
  Zap,
  Sparkles,
  GraduationCap,
} from "lucide-react";
import { PageHero, SectionEyebrow } from "@/components/ui-bits";

const SITE_URL = "https://ogonotary.com";
const PDF_URL = "/downloads/OGO_Mobile_Notary_Field_SOP_Public_Edition_2026.pdf";
const PDF_FULL_TITLE =
  "OGO Mobile Notary Field Execution Standard — National Best-Practice Framework with Florida-Specific Compliance Notes";

export default function MobileNotaryFieldSopPage() {
  const canonical = `${SITE_URL}/mobile-notary-field-sop`;

  function handleDownloadClick() {
    // Lightweight event marker so any existing analytics that watches the DOM
    // for gtag / dataLayer / GA can attach later without touching this page.
    try {
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({ event: "mobile_notary_sop_download" });
      }
    } catch (_e) {
      // never block the download because of analytics
    }
  }

  return (
    <>
      <Helmet>
        <title>Free Mobile Notary Field SOP | OGO Notary</title>
        <meta
          name="description"
          content="Download the free OGO Mobile Notary Field SOP with practical workflows for mobile appointments, loan signings, estate planning, hospital signings, quality control, and Florida-specific compliance notes."
        />
        <meta
          name="keywords"
          content="mobile notary SOP, notary field workflow, loan signing procedure, estate planning notary, hospital notary, Florida notary compliance, mobile notary checklist, notary best practices, OGO Notary"
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta
          property="og:title"
          content="Free Mobile Notary Field SOP | OGO Notary"
        />
        <meta
          property="og:description"
          content="A free, practical mobile-notary field workflow covering preparation, travel, signing-table execution, quality control, loan signings, estate planning, hospital and bedside procedures, and Florida-specific compliance notes."
        />
        <meta property="og:url" content={canonical} />
        <meta
          property="og:image"
          content={`${SITE_URL}/img/mobile-notary-sop-og.png`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Free Mobile Notary Field SOP | OGO Notary"
        />
        <meta
          name="twitter:description"
          content="Free mobile-notary operational playbook — download instantly, no signup required."
        />
      </Helmet>

      {/* HERO */}
      <PageHero
        eyebrow="FREE · No email required"
        title="Free Mobile Notary Field SOP."
        lead="National Best-Practice Framework with Florida-Specific Compliance Notes. Walk into your next mobile appointment with a repeatable process — not a handful of things you're trying to remember."
      />

      <section className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12 sm:py-16">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                Whether you&apos;re preparing for a general mobile
                notarization, loan signing, estate-planning appointment, or
                hospital/bedside signing, this free OGO Notary resource provides
                a practical field workflow designed to help you{" "}
                <span className="text-[#D4AF37]">prepare</span>,{" "}
                <span className="text-[#D4AF37]">execute</span>,{" "}
                <span className="text-[#D4AF37]">quality-check</span>, and{" "}
                <span className="text-[#D4AF37]">close out</span> appointments
                professionally.
              </p>

              <a
                href={PDF_URL}
                onClick={handleDownloadClick}
                data-testid="sop-hero-download-cta"
                data-analytics-event="mobile_notary_sop_download"
                target="_blank"
                rel="noopener"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] px-8 py-4 text-xs sm:text-sm uppercase tracking-[0.22em] font-bold transition-colors"
              >
                <Download className="w-4 h-4" />
                Download the Free SOP
              </a>

              <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-slate-500">
                PDF · No email · Instant download
              </p>
            </div>

            <div className="lg:col-span-5">
              <PdfMockup />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST / GIVE MESSAGE */}
      <section
        data-testid="sop-trust-banner"
        className="border-b border-white/10 bg-[#D4AF37]"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-8 sm:py-10 text-center">
          <p className="text-[#142339] text-lg sm:text-xl font-bold uppercase tracking-[0.14em] leading-snug">
            No email. No purchase. Just a free educational resource for fellow Notaries.
          </p>
          <p className="mt-4 text-[#142339]/85 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            At OGO Notary, we believe useful information should actually be
            useful. If this resource helps you serve one signer better, avoid
            one preventable mistake, or operate your business more
            professionally, then it has accomplished its purpose.
          </p>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="border-b border-white/10 py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <SectionEyebrow className="mb-4">Contents</SectionEyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight max-w-3xl">
            What&apos;s Inside the Mobile Notary Field SOP?
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <InsideBlock
              icon={ClipboardCheck}
              eyebrow="4-Phase Mobile Notary Workflow"
              items={[
                "Pre-Appointment Preparation",
                "Travel & Route Logistics",
                "Signing-Table Execution",
                "Post-Signing Quality Control",
              ]}
            />
            <InsideBlock
              icon={FileSignature}
              eyebrow="Signing Practices"
              items={[
                "Loan & Real Estate Signing Practices",
                "Estate Planning & Will Signing Practices",
                "Hospital & Bedside Procedures",
                "General Notarization Practices",
              ]}
            />
            <InsideBlock
              icon={Landmark}
              eyebrow="Florida-Specific Compliance Notes"
              items={[
                "State-Law Compliance Reminders",
                "FL Chapter 117 Operational Guidance",
              ]}
            />
            <InsideBlock
              icon={BookOpen}
              eyebrow="National Best-Practice Guidance"
              items={[
                "Cross-jurisdictional operational patterns",
                "Standards notaries can adapt to their commission",
              ]}
            />
          </div>
        </div>
      </section>

      {/* NATIONAL vs FLORIDA */}
      <section
        data-testid="sop-national-vs-florida"
        className="border-b border-white/10 py-16 sm:py-20 bg-[#0A1020]"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <SectionEyebrow className="mb-4">
            Built for Notaries Everywhere
          </SectionEyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-white leading-tight tracking-tight">
            Built for Notaries Nationwide — With Florida-Specific Notes Clearly Identified
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed max-w-3xl">
            Notary law is state-specific. There is no single nationwide notary
            code. This guide separates generally useful mobile-notary
            operational practices from Florida-specific requirements.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <TagRow
              tag="GENERAL PRACTICE"
              tagTone="general"
              text="Identifies operational guidance that may be useful to mobile notaries but should not be interpreted as law in every state."
            />
            <TagRow
              tag="FLORIDA NOTE"
              tagTone="florida"
              text="Identifies information specifically related to Florida notary law and practice."
            />
          </div>

          <p className="mt-8 text-slate-400 text-sm italic max-w-3xl leading-relaxed">
            Notaries commissioned outside Florida should always verify
            requirements with their own commissioning authority, Secretary of
            State, and applicable statutes and administrative rules.
          </p>

          <div className="mt-10">
            <a
              href={PDF_URL}
              onClick={handleDownloadClick}
              data-testid="sop-mid-download-cta"
              data-analytics-event="mobile_notary_sop_download"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#142339] px-6 py-3.5 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Download className="w-4 h-4" />
              Download the Free SOP
            </a>
          </div>
        </div>
      </section>

      {/* GIVE FRAMEWORK */}
      <section
        data-testid="sop-give-framework"
        className="border-b border-white/10 py-16 sm:py-20"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <SectionEyebrow className="mb-4">The OGO Belief</SectionEyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight">
            Why Are We Giving This Away?
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed max-w-3xl">
            Because OGO Notary was built around a simple belief:{" "}
            <span className="text-[#D4AF37] font-semibold">What We Do Matters.</span>
          </p>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <GiveCard
              letter="G"
              title="Give"
              text="Give back in service to others."
              icon={HandHeart}
            />
            <GiveCard
              letter="I"
              title="Impact"
              text="Impact the lives of others so they can better serve their businesses, themselves, their families, and their communities."
              icon={Zap}
            />
            <GiveCard
              letter="V"
              title="Value"
              text="Create genuine value in every aspect of what we do."
              icon={Sparkles}
            />
            <GiveCard
              letter="E"
              title="Educate & Empower"
              text="Provide information, education, and practical resources that help others grow, serve, and pay it forward."
              icon={GraduationCap}
            />
          </div>

          <p
            data-testid="sop-give-close"
            className="mt-12 font-serif-display italic text-[#D4AF37] text-xl sm:text-2xl leading-relaxed border-l-2 border-[#D4AF37] pl-5"
          >
            Give first. Help someone move forward. Trust God with tomorrow.
          </p>
        </div>
      </section>

      {/* SECOND CTA */}
      <section
        data-testid="sop-final-cta"
        className="border-b border-white/10 py-16 sm:py-20 bg-[#0A1020]"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <SectionEyebrow className="mb-4">Take It With You</SectionEyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight">
            Ready to Build a More Consistent Mobile Notary Workflow?
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed max-w-3xl mx-auto">
            Download the OGO Mobile Notary Field Execution Standard and keep it
            available as a practical reference while developing your own
            procedures.
          </p>
          <a
            href={PDF_URL}
            onClick={handleDownloadClick}
            data-testid="sop-final-download-cta"
            data-analytics-event="mobile_notary_sop_download"
            target="_blank"
            rel="noopener"
            className="mt-8 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] px-8 py-4 text-xs sm:text-sm uppercase tracking-[0.22em] font-bold transition-colors"
          >
            <Download className="w-4 h-4" />
            Download the Free SOP
          </a>
        </div>
      </section>

      {/* MORE FREE RESOURCES */}
      <section className="border-b border-white/10 py-14 sm:py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h3 className="font-serif-display text-2xl sm:text-3xl text-white leading-tight">
            Looking for More Notary Resources?
          </h3>
          <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl mx-auto">
            Explore additional free guides, educational articles, tools, and
            practical resources from OGO Notary &amp; Signing Services.
          </p>
          <Link
            to="/resources"
            data-testid="sop-explore-resources-cta"
            className="mt-8 inline-flex items-center justify-center gap-2 border border-white/20 text-slate-200 hover:border-[#D4AF37] hover:text-[#D4AF37] px-6 py-3.5 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
          >
            <ArrowUpRight className="w-4 h-4" />
            Explore Free Resources
          </Link>
        </div>
      </section>

      {/* DISCLAIMER */}
      <aside
        data-testid="sop-disclaimer"
        className="border-b border-white/10 bg-[#0A1020]"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-10">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-3">
                Educational Resource Disclaimer
              </div>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                This resource is provided for educational and informational
                purposes only and does not constitute legal, tax, financial,
                medical, title, lending, or other professional advice. Notary
                laws and requirements vary by state and may change. Notaries
                are responsible for verifying current laws, administrative
                rules, commissioning guidance, document-specific requirements,
                and lawful hiring-party instructions applicable to their
                commission and assignment.
              </p>
              <p className="text-slate-300 text-sm leading-relaxed">
                When questions involve legal interpretation or document
                execution requirements, users should consult the document
                issuer, receiving agency, title or lending professional,
                commissioning authority, or a licensed attorney as appropriate.
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

function PdfMockup() {
  return (
    <div
      data-testid="sop-pdf-mockup"
      className="relative w-full aspect-[3/4] max-w-sm mx-auto"
    >
      {/* Back page */}
      <div className="absolute inset-0 translate-x-3 translate-y-3 bg-[#0A1020] border border-white/10" />
      {/* Front page */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#142339] via-[#1A2B45] to-[#0F1B2E] border border-[#D4AF37]/40 shadow-[0_20px_60px_rgba(0,0,0,0.5)] p-6 flex flex-col">
        <div className="text-[9px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
          OGO Notary &amp; Signing Services
        </div>
        <div className="mt-6 text-[9px] uppercase tracking-[0.28em] text-[#D4AF37]">
          Field Playbook
        </div>
        <div className="mt-2 font-serif-display text-xl text-white leading-tight">
          Mobile Notary Field Execution Standard
        </div>
        <div className="mt-3 text-[10px] text-slate-300 leading-relaxed">
          National Best-Practice Framework with Florida-Specific Compliance
          Notes
        </div>

        <div className="mt-6 space-y-2.5 flex-1">
          {[
            [Truck, "Pre-Appointment Preparation"],
            [MapPin, "Travel & Route Logistics"],
            [FileSignature, "Signing-Table Execution"],
            [ClipboardCheck, "Post-Signing Quality Control"],
            [Home, "Loan & Estate Practices"],
            [Stethoscope, "Hospital & Bedside Procedures"],
          ].map(([Icon, label], i) => (
            <div key={i} className="flex items-center gap-2 text-[10px] text-slate-300">
              <Icon className="w-3 h-3 text-[#D4AF37] shrink-0" />
              <span>{label}</span>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-white/10 text-[9px] uppercase tracking-[0.22em] text-[#D4AF37]">
          Free · No Email Required
        </div>
      </div>
    </div>
  );
}

function InsideBlock({ icon: Icon, eyebrow, items }) {
  return (
    <div className="border border-white/10 bg-[#1A2B45] p-6 sm:p-7">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
          <Icon className="w-5 h-5" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
          {eyebrow}
        </div>
      </div>
      <ul className="space-y-3">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <CheckCircle2 className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />
            <span className="text-slate-200 text-sm leading-relaxed">
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TagRow({ tag, tagTone, text }) {
  const toneClass =
    tagTone === "florida"
      ? "bg-[#D4AF37] text-[#142339] border-[#D4AF37]"
      : "bg-transparent text-[#D4AF37] border-[#D4AF37]/70";
  return (
    <div className="border border-white/10 bg-[#142339] p-5">
      <span
        className={`inline-block text-[10px] uppercase tracking-[0.22em] font-bold border px-2.5 py-1 mb-3 ${toneClass}`}
      >
        {tag}
      </span>
      <p className="text-slate-300 text-sm leading-relaxed">{text}</p>
    </div>
  );
}

function GiveCard({ letter, title, text, icon: Icon }) {
  return (
    <div className="border border-white/10 bg-[#1A2B45] p-6 flex flex-col">
      <div className="flex items-baseline gap-3 mb-4">
        <div className="font-serif-display text-4xl text-[#D4AF37] leading-none">
          {letter}
        </div>
        <div className="w-8 h-8 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0">
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2">
        {title}
      </div>
      <p className="text-slate-300 text-sm leading-relaxed flex-1">{text}</p>
    </div>
  );
}
